import { useEffect, useRef, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';

export interface GeoJsonComponentProps {
  geoJsonData: any;
  selectedNetworkType: string;
  map: mapboxgl.Map;
  shouldFitBounds: boolean;
  showBoundingBox: boolean;
  showGeoPolygon: boolean;
}

export default function GeoJsonComponent({
  geoJsonData,
  selectedNetworkType,
  map,
  shouldFitBounds,
  showBoundingBox,
  showGeoPolygon,
}: GeoJsonComponentProps) {
  const sourceId = 'measurement-data';
  const layerId = 'measurement-layer';
  const bboxSourceId = 'bbox-source';
  const bboxLayerId = 'bbox-layer';
  const userPolygonSourceId = 'user-polygon-source';
  const userPolygonLayerId = 'user-polygon-layer';

  const getFirstSymbolLayerId = () => {
    const layers = map.getStyle().layers;
    for (let i = 0; i < layers.length; i++) {
      if (layers[i].type === 'symbol') {
        return layers[i].id;
      }
    }
    return undefined;
  };

  const addLayers = useCallback(() => {
    if (!map || !geoJsonData) return;

    // Handle bounding box
    if (geoJsonData.bbox && showBoundingBox) {
      const bboxPolygon = {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [geoJsonData.bbox[0], geoJsonData.bbox[1]],
              [geoJsonData.bbox[2], geoJsonData.bbox[1]],
              [geoJsonData.bbox[2], geoJsonData.bbox[3]],
              [geoJsonData.bbox[0], geoJsonData.bbox[3]],
              [geoJsonData.bbox[0], geoJsonData.bbox[1]],
            ],
          ],
        },
      } as GeoJSON.Feature;

      if (!map.getSource(bboxSourceId)) {
        map.addSource(bboxSourceId, {
          type: 'geojson',
          data: bboxPolygon,
        });
      } else {
        (map.getSource(bboxSourceId) as mapboxgl.GeoJSONSource).setData(
          bboxPolygon,
        );
      }

      if (!map.getLayer(bboxLayerId)) {
        map.addLayer(
          {
            id: bboxLayerId,
            type: 'line',
            source: bboxSourceId,
            paint: {
              'line-color': '#a9cce3',
              'line-width': 3,
              'line-opacity': 1,
            },
          },
          getFirstSymbolLayerId(),
        );
      }
    }

    // Handle user-defined polygon
    if (geoJsonData.bounding_polygon && showGeoPolygon) {
      const userPolygon = {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates: geoJsonData.bounding_polygon,
        },
      } as GeoJSON.Feature;

      if (!map.getSource(userPolygonSourceId)) {
        map.addSource(userPolygonSourceId, {
          type: 'geojson',
          data: userPolygon,
        });
      } else {
        (map.getSource(userPolygonSourceId) as mapboxgl.GeoJSONSource).setData(
          userPolygon,
        );
      }

      if (!map.getLayer(userPolygonLayerId)) {
        map.addLayer(
          {
            id: userPolygonLayerId,
            type: 'line',
            source: userPolygonSourceId,
            paint: {
              'line-color': '#FFA500', // orange
              'line-width': 3,
              'line-opacity': 0.8,
            },
          },
          getFirstSymbolLayerId(),
        );
      }
    }

    // Handle measurement data
    if (!geoJsonData.features?.length) return;

    // Add source if it doesn't exist
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, {
        type: 'geojson',
        data: geoJsonData,
      });
    } else {
      // Update existing source
      (map.getSource(sourceId) as mapboxgl.GeoJSONSource).setData(geoJsonData);
    }

    // Add layer if it doesn't exist
    if (!map.getLayer(layerId)) {
      map.addLayer(
        {
          id: layerId,
          type: 'fill',
          source: sourceId,
          paint: {
            'fill-color': [
              'case',
              ['==', ['get', 'empty'], true],
              '#999999', // mid-grey for empty bins
              ['get', 'color'],
            ],
            'fill-opacity': 0.7,
          },
        },
        getFirstSymbolLayerId(),
      );
    }
  }, [map, geoJsonData, showBoundingBox, showGeoPolygon]);

  useEffect(() => {
    if (!geoJsonData) return;

    addLayers();

    // Handle visibility toggles
    if (!showBoundingBox) {
      if (map.getLayer(bboxLayerId)) {
        map.removeLayer(bboxLayerId);
      }
      if (map.getSource(bboxSourceId)) {
        map.removeSource(bboxSourceId);
      }
    }

    if (!showGeoPolygon) {
      if (map.getLayer(userPolygonLayerId)) {
        map.removeLayer(userPolygonLayerId);
      }
      if (map.getSource(userPolygonSourceId)) {
        map.removeSource(userPolygonSourceId);
      }
    }

    // Fit map to bounds of the data
    if (shouldFitBounds) {
      const bounds = new mapboxgl.LngLatBounds();
      geoJsonData.features.forEach((feature: any) => {
        const coords = feature.geometry.coordinates[0];
        coords.forEach((coord: [number, number]) => {
          bounds.extend(coord as [number, number]);
        });
      });
      map.fitBounds(bounds, {
        padding: 50,
        duration: 0, // Disable animation
      });
    }

    // Cleanup function
    return () => {
      if (map && map.getStyle()) {
        // Check if map is still valid
        if (map.getLayer(layerId)) {
          map.removeLayer(layerId);
        }
        if (map.getSource(sourceId)) {
          map.removeSource(sourceId);
        }
        if (map.getLayer(bboxLayerId)) {
          map.removeLayer(bboxLayerId);
        }
        if (map.getSource(bboxSourceId)) {
          map.removeSource(bboxSourceId);
        }
        if (map.getLayer(userPolygonLayerId)) {
          map.removeLayer(userPolygonLayerId);
        }
        if (map.getSource(userPolygonSourceId)) {
          map.removeSource(userPolygonSourceId);
        }
      }
    };
  }, [
    geoJsonData,
    map,
    shouldFitBounds,
    showBoundingBox,
    showGeoPolygon,
    addLayers,
  ]);

  // Effect to re-add layers when style changes
  useEffect(() => {
    if (!map) return;
    map.on('style.load', addLayers);

    return () => {
      if (!map) return;
      map.off('style.load', addLayers);
    };
  }, [map, addLayers]);

  return null;
}
